import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import SntRadio from "@cleanup/components/SntRadio/SntRadio";
import useOuterClick from "@cleanup/hooks/useOutsideElement";
import GeneralFooterFilter from "@cleanup/components/GeneralFooterFilter/GeneralFooterFilter";
import ExcludeLabel from "@cleanup/components/AdvancedFilters/ExcludeLabel";
import { Formik } from "formik";
import * as Yup from "yup";
import moment from "@/constants/SensolusMoment";
import StringUtils from "@/utils/StringUtils";
import { Button, Col, Container, Form, Row } from "react-bootstrap";
import { AdvanceFilter, FilterBody, FilterHeaderStyle } from "@cleanup/components/AdvancedFilters/FilterStyle";
import IcomoonIcon from "@icons/IcomoonIcon";
import SntCloseSmallIcon from "@icons/SntCloseSmallIcon";
import SntArrowDownIcon from "@icons/SntArrowDownIcon";
import usePositionFilter from "@cleanup/hooks/usePositionFilter";
import { translate } from "@/hooks/useLanguage";
import { LanguageKey } from "@/types/generated/LanguageKey";

interface FilterTypeSettings {
  excludeTime?: boolean;
}

interface Descriptor {
  _idx: string;
  key?: string;
  label?: string;
  description: string;
  filterTypeSettings: FilterTypeSettings;
}

interface FilterValue {
  dateFilterType?: string;
  lastValue?: number;
  lastUnit?: string;
  intervalSign?: string;
  absoluteStartDate?: string;
  absoluteEndDate?: string;
  relativeStartDate?: string;
  relativeEndDate?: string;
  month?: number | string;
}

interface FilterData {
  filterKey?: string;
  filterType?: string;
  categoryId?: string;
  searchType?: string;
  filterValue?: FilterValue;
  notFilter?: boolean;
  idx?: string;
}

interface DateFilterProps {
  descriptor: Descriptor;
  data?: FilterData;
  onChange?: (data: FilterData | { idx: string; data: null }) => void;
  disabled?: boolean;
}

interface InitialValues {
  greater: string;
  greaterUnit: string;
  greaterIntervalSign: string;
  smaller: string;
  smallerUnit: string;
  smallerIntervalSign: string;
  absoluteStartDate: string;
  absoluteEndDate: string;
  relativeStartDate: string;
  relativeEndDate: string;
  month: number | string;
}

interface OptionItem {
  value: string;
  label: string;
}

const DateFilter: React.FC<DateFilterProps> = ({
  descriptor,
  data = {},
  onChange,
  disabled = false,
}) => {
  const [isShow, setShow] = useState(false);
  const [title, setTitle] = useState("");
  const [errors, setErrors] = useState<Record<string, unknown> | null>(null);

  const [selectedValue, setSelectedValue] = useState("");

  const [notFilter, setNotFilter] = useState(false);

  const popupStyleRef = useRef<Record<string, unknown>>({});
  const { popupRef, buttonRef, getPosition } = usePositionFilter();

  const optionsUnitDate: OptionItem[] = [
    { value: "MONTHS", label: translate(LanguageKey.months_key) },
    { value: "WEEKS", label: translate(LanguageKey.weeks_key) },
    { value: "DAYS", label: translate(LanguageKey.maintenance_unit_DAYS_key) },
    { value: "HOURS", label: translate(LanguageKey.hours_key) },
    { value: "MINUTES", label: translate(LanguageKey.minutes_key) },
  ];

  const optionsMonths: OptionItem[] = [
    { value: "1", label: translate(LanguageKey.january_key) },
    { value: "2", label: translate(LanguageKey.february_key) },
    { value: "3", label: translate(LanguageKey.march_key) },
    { value: "4", label: translate(LanguageKey.april_key) },
    { value: "5", label: translate(LanguageKey.may_key) },
    { value: "6", label: translate(LanguageKey.june_key) },
    { value: "7", label: translate(LanguageKey.july_key) },
    { value: "8", label: translate(LanguageKey.august_key) },
    { value: "9", label: translate(LanguageKey.september_key) },
    { value: "10", label: translate(LanguageKey.october_key) },
    { value: "11", label: translate(LanguageKey.november_key) },
    { value: "12", label: translate(LanguageKey.december_key) },
  ];

  const [initialValues, setInitialValues] = useState<InitialValues>({
    greater: "1",
    greaterUnit: "MONTHS",
    greaterIntervalSign: "PAST",
    smaller: "1",
    smallerUnit: "MONTHS",
    smallerIntervalSign: "PAST",
    absoluteStartDate: new Date().toLocaleDateString("en-CA"),
    absoluteEndDate: new Date().toLocaleDateString("en-CA"),
    relativeStartDate: "-1M1w",
    relativeEndDate: "+1h2m",
    month: new Date().getMonth() + 1,
  });

  const patt = /^[-|+](([0-9]+M)?([0-9]+w)?([0-9]+d)?([0-9]+h)?([0-9]+m)?)$/g;

  const validationSchema = Yup.object({
    greater: Yup.number()
      .required(StringUtils.replaceStr(translate(LanguageKey.greater_than_key), "0"))
      .moreThan(0),
    smaller: Yup.number()
      .required(StringUtils.replaceStr(translate(LanguageKey.greater_than_key), "0"))
      .moreThan(0),
    absoluteStartDate: Yup.date().required(
      StringUtils.replaceStr(translate(LanguageKey.valid_start_end_days_key), "0")
    ),
    absoluteEndDate: Yup.date().required(
      StringUtils.replaceStr(translate(LanguageKey.valid_start_end_days_key), "0")
    ),
    relativeStartDate: Yup.string()
      .required(StringUtils.replaceStr(translate(LanguageKey.regex_error_key), "0"))
      .matches(patt, StringUtils.replaceStr(translate(LanguageKey.regex_error_key), "0")),
    relativeEndDate: Yup.string()
      .required(StringUtils.replaceStr(translate(LanguageKey.regex_error_key), "0"))
      .matches(patt, StringUtils.replaceStr(translate(LanguageKey.regex_error_key), "0")),
  });

  const optionIntervalSign: OptionItem[] = [
    { value: "PAST", label: translate(LanguageKey.ago_key) },
    { value: "FUTURE", label: translate(LanguageKey.from_now_key) },
  ];

  useEffect(() => {
    const { filterValue } = data;
    let _title = translate(LanguageKey.all_key);
    let operator = "";
    if (filterValue) {
      operator = filterValue.dateFilterType || "";

      const unitDate = optionsUnitDate.find(
        (m) => m.value === filterValue.lastUnit
      );

      if (operator === "MORE_THAN") {
        _title =
          translate(LanguageKey.more_than_key) +
          " " +
          filterValue.lastValue +
          " " +
          (unitDate?.label || "");
        setInitialValues((previous) => {
          return {
            ...previous,
            greater: String(filterValue.lastValue),
            greaterUnit: filterValue.lastUnit || "MONTHS",
            greaterIntervalSign: filterValue.intervalSign || "PAST",
          };
        });
      } else if (operator === "LESS_THAN") {
        _title =
          translate(LanguageKey.less_than_key) +
          " " +
          filterValue.lastValue +
          " " +
          (unitDate?.label || "");
        setInitialValues((previous) => {
          return {
            ...previous,
            smaller: String(filterValue.lastValue),
            smallerUnit: filterValue.lastUnit || "MONTHS",
            smallerIntervalSign: filterValue.intervalSign || "PAST",
          };
        });
      } else if (operator === "BETWEEN_ABSOLUTE") {
        _title =
          new Date(filterValue.absoluteStartDate || "").toDateString() +
          "-" +
          new Date(filterValue.absoluteEndDate || "").toDateString();
        setInitialValues((previous) => {
          return {
            ...previous,
            absoluteStartDate: moment(filterValue.absoluteStartDate).format(
              "yyyy-MM-DD"
            ),
            absoluteEndDate: moment(filterValue.absoluteEndDate).format(
              "yyyy-MM-DD"
            ),
          };
        });
      } else if (operator === "BETWEEN_RELATIVE") {
        _title = `${filterValue.relativeStartDate} ${translate(LanguageKey.till_key)} ${filterValue.relativeEndDate}`;
        setInitialValues((previous) => {
          return {
            ...previous,
            relativeStartDate: filterValue.relativeStartDate || "",
            relativeEndDate: filterValue.relativeEndDate || "",
          };
        });
      } else if (operator === "MONTH") {
        const monthValue = String(filterValue.month ?? "");
        const monthOption = optionsMonths.find(
          (m) => m.value === monthValue
        );
        if (monthOption?.label) {
          _title = monthOption?.label;
        }
        setInitialValues((previous) => {
          return {
            ...previous,
            month: monthOption?.value ?? new Date().getMonth() + 1,
          };
        });
      } else if (operator === "NULL") {
        _title = translate(LanguageKey.has_no_value_key);
        setInitialValues((previous) => {
          return {
            ...previous,
          };
        });
      }
    }
    setSelectedValue(operator);
    setNotFilter(Boolean(data.notFilter));
    setTitle(_title);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, data.filterValue]);

  const getData = useCallback(() => {
    const newData = JSON.parse(JSON.stringify(data)) as FilterData;

    const filterValue: FilterValue = {
      dateFilterType: undefined,
      lastValue: undefined,
      lastUnit: undefined,
      intervalSign: undefined,
      absoluteStartDate: undefined,
      absoluteEndDate: undefined,
      relativeStartDate: undefined,
      relativeEndDate: undefined,
    };

    filterValue.dateFilterType = selectedValue;

    if (selectedValue === "MORE_THAN") {
      filterValue.lastValue = Number.parseInt(initialValues.greater);
      filterValue.lastUnit = initialValues.greaterUnit;
      filterValue.intervalSign = initialValues.greaterIntervalSign;
      newData.filterValue = filterValue;
    } else if (selectedValue === "LESS_THAN") {
      filterValue.lastValue = Number.parseInt(initialValues.smaller);
      filterValue.lastUnit = initialValues.smallerUnit;
      filterValue.intervalSign = initialValues.smallerIntervalSign;
      newData.filterValue = filterValue;
    } else if (selectedValue === "BETWEEN_ABSOLUTE") {
      filterValue.absoluteStartDate = moment(
        initialValues.absoluteStartDate
      ).format();
      if (descriptor.filterTypeSettings.excludeTime) {
        filterValue.absoluteStartDate = moment
          .utc(initialValues.absoluteStartDate)
          .format();
      }
      filterValue.absoluteEndDate = moment(initialValues.absoluteEndDate)
        .endOf("day")
        .format();
      if (descriptor.filterTypeSettings.excludeTime) {
        filterValue.absoluteEndDate = moment
          .utc(initialValues.absoluteEndDate)
          .endOf("day")
          .format();
      }
      newData.filterValue = filterValue;
    } else if (selectedValue === "BETWEEN_RELATIVE") {
      filterValue.relativeStartDate = initialValues.relativeStartDate;
      filterValue.relativeEndDate = initialValues.relativeEndDate;
      newData.filterValue = filterValue;
    } else if (selectedValue === "MONTH") {
      filterValue.month = initialValues.month;
      newData.filterValue = filterValue;
    } else if (selectedValue === "NULL") {
      newData.filterValue = filterValue;
    }

    newData.notFilter = notFilter;

    return newData;
  }, [
    data,
    selectedValue,
    notFilter,
    initialValues,
    descriptor.filterTypeSettings.excludeTime,
  ]);

  const refWrapper = useOuterClick(() => {
    if (isShow && errors && Object.keys(errors).length === 0) {
      setShow(false);
      if (isChanged()) {
        if (onChange) {
          onChange(getData());
        }
      }
    }
  });

  const isChanged = useCallback(() => {
    const oldDataFilter = data.filterValue || {};
    const newData = getData();
    const newDataFilter = newData.filterValue || {};

    if (
      oldDataFilter.absoluteEndDate === newDataFilter.absoluteEndDate &&
      oldDataFilter.absoluteStartDate === newDataFilter.absoluteStartDate &&
      oldDataFilter.dateFilterType === newDataFilter.dateFilterType &&
      oldDataFilter.intervalSign === newDataFilter.intervalSign &&
      oldDataFilter.lastUnit === newDataFilter.lastUnit &&
      oldDataFilter.lastValue === newDataFilter.lastValue &&
      oldDataFilter.relativeEndDate === newDataFilter.relativeEndDate &&
      oldDataFilter.relativeStartDate === newDataFilter.relativeStartDate &&
      (data.notFilter === newData.notFilter ||
        (data.notFilter === undefined && newData.notFilter === false) ||
        (data.notFilter === false && newData.notFilter === undefined)) &&
      oldDataFilter.month === newDataFilter.month
    ) {
      return false;
    }
    return true;
  }, [data, getData]);

  const onClickButton = () => {
    if (isShow) {
      setShow(false);
      if (isChanged()) {
        if (onChange) {
          onChange(getData());
        }
      }
    } else {
      popupStyleRef.current = getPosition();
      setShow(true);
    }
  };

  const onAppliedFilter = () => {
    setShow(false);
    if (isChanged()) {
      if (onChange) {
        onChange(getData());
      }
    }
  };

  const onClearFilter = () => {
    setSelectedValue("");
    setNotFilter(false);
    setShow(false);
    if (onChange) {
      onChange({
        idx: descriptor._idx,
        data: null,
      });
    }
  };

  const excludeLabel = useMemo(() => {
    setNotFilter(Boolean(data.notFilter));

    return data.notFilter && <ExcludeLabel />;
  }, [data.notFilter]);

  return (
    <AdvanceFilter ref={refWrapper}>
      <Button
        ref={buttonRef}
        variant="sensolus-greylight"
        title={descriptor.description}
        disabled={disabled}
        onClick={onClickButton}
      >
        {selectedValue && (
          <SntCloseSmallIcon
            className="me-1"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onClearFilter();
            }}
          />
        )}
        <span>{descriptor.label || descriptor.description}</span>:{" "}
        <span>{excludeLabel}</span>
        <FilterHeaderStyle>{title}</FilterHeaderStyle>
        <SntArrowDownIcon />
      </Button>
      <FilterBody
        ref={popupRef}
        style={{ display: isShow ? "block" : "none", ...popupStyleRef.current }}
      >
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          enableReinitialize={true}
          onSubmit={() => {
            onAppliedFilter();
          }}
        >
          {({ handleSubmit, handleChange, values, errors }) => {
            setTimeout(() => setErrors(errors), 0);
            return (
              <form onSubmit={handleSubmit}>
                <Container>
                  <div className="mb-3 mt-3">
                    <SntRadio
                      value="MORE_THAN"
                      selectedValue={selectedValue}
                      onChange={(value) => value !== undefined && setSelectedValue(value)}
                      label={translate(LanguageKey.more_than_key)}
                    />
                  </div>
                  <Row className="mb-3">
                    <Col className="d-flex">
                      <Form.Control
                        name="greater"
                        value={values.greater}
                        type="number"
                        min="0"
                        className={` me-2 ${
                          errors.greater && selectedValue === "MORE_THAN"
                            ? "is-invalid"
                            : "is-valid"
                        }`}
                        disabled={selectedValue !== "MORE_THAN"}
                        style={{ width: "3.5rem" }}
                        onChange={(e) => {
                          handleChange(e);
                          setInitialValues({
                            ...initialValues,
                            greater: e.target.value,
                          });
                        }}
                      />
                      <Form.Select
                        name="greaterUnit"
                        value={values.greaterUnit}
                        className="me-2"
                        disabled={selectedValue !== "MORE_THAN"}
                        style={{ width: "8.5rem" }}
                        onChange={(e) => {
                          handleChange(e);
                          setInitialValues({
                            ...initialValues,
                            greaterUnit: e.target.value,
                          });
                        }}
                      >
                        {optionsUnitDate.map((greaterUnit) => (
                          <option
                            key={greaterUnit.value}
                            value={greaterUnit.value}
                          >
                            {greaterUnit.label}
                          </option>
                        ))}
                      </Form.Select>
                      <Form.Select
                        name="greaterIntervalSign"
                        value={values.greaterIntervalSign}
                        disabled={selectedValue !== "MORE_THAN"}
                        onChange={(e) => {
                          handleChange(e);
                          setInitialValues({
                            ...initialValues,
                            greaterIntervalSign: e.target.value,
                          });
                        }}
                      >
                        {optionIntervalSign.map((optionIntervalSign) => (
                          <option
                            key={optionIntervalSign.value}
                            value={optionIntervalSign.value}
                          >
                            {optionIntervalSign.label}
                          </option>
                        ))}
                      </Form.Select>
                    </Col>
                    <Form.Control.Feedback
                      type="invalid"
                      style={{ display: errors.greater ? "block" : "none" }}
                    >
                      {errors.greater as string}
                    </Form.Control.Feedback>
                  </Row>

                  <div className="mb-3">
                    <SntRadio
                      value="LESS_THAN"
                      selectedValue={selectedValue}
                      onChange={(value) => value !== undefined && setSelectedValue(value)}
                      label={translate(LanguageKey.less_than_key)}
                    />
                  </div>
                  <Row className="mb-3">
                    <Col className="d-flex">
                      <Form.Control
                        name="smaller"
                        value={values.smaller}
                        type="number"
                        min="0"
                        className={`float-start  me-2 ${
                          errors.smaller && selectedValue === "LESS_THAN"
                            ? "is-invalid"
                            : "is-valid"
                        }`}
                        disabled={selectedValue !== "LESS_THAN"}
                        style={{ width: "3.5rem" }}
                        onChange={(e) => {
                          handleChange(e);
                          setInitialValues({
                            ...initialValues,
                            smaller: e.target.value,
                          });
                        }}
                      />
                      <Form.Select
                        name="smallerUnit"
                        value={values.smallerUnit}
                        className="float-start  me-2"
                        disabled={selectedValue !== "LESS_THAN"}
                        style={{ width: "8.5rem" }}
                        onChange={(e) => {
                          handleChange(e);
                          setInitialValues({
                            ...initialValues,
                            smallerUnit: e.target.value,
                          });
                        }}
                      >
                        {optionsUnitDate.map((smallerUnit) => (
                          <option
                            key={smallerUnit.value}
                            value={smallerUnit.value}
                          >
                            {smallerUnit.label}
                          </option>
                        ))}
                      </Form.Select>
                      <Form.Select
                        name="smallerIntervalSign"
                        value={values.smallerIntervalSign}
                        className="float-start"
                        disabled={selectedValue !== "LESS_THAN"}
                        onChange={(e) => {
                          handleChange(e);
                          setInitialValues({
                            ...initialValues,
                            smallerIntervalSign: e.target.value,
                          });
                        }}
                      >
                        {optionIntervalSign.map((optionIntervalSign) => (
                          <option
                            key={optionIntervalSign.value}
                            value={optionIntervalSign.value}
                          >
                            {optionIntervalSign.label}
                          </option>
                        ))}
                      </Form.Select>
                    </Col>
                    <Form.Control.Feedback
                      type="invalid"
                      style={{ display: errors.smaller ? "block" : "none" }}
                    >
                      {errors.smaller as string}
                    </Form.Control.Feedback>
                  </Row>

                  <Row className="mb-3">
                    <Col>
                      <SntRadio
                        value="BETWEEN_ABSOLUTE"
                        selectedValue={selectedValue}
                        onChange={(value) => value !== undefined && setSelectedValue(value)}
                        label={translate(LanguageKey.between_dates_key)}
                      />
                    </Col>
                  </Row>
                  <Row className="mb-3">
                    <Col className="d-flex justify-content-between">
                      <Form.Control
                        name="absoluteStartDate"
                        value={values.absoluteStartDate}
                        type="date"
                        className={` me-2 ${
                          (errors.absoluteStartDate ||
                            errors.absoluteEndDate) &&
                          selectedValue === "BETWEEN_ABSOLUTE"
                        }`}
                        style={{ position: "relative" }}
                        disabled={selectedValue !== "BETWEEN_ABSOLUTE"}
                        onChange={(e) => {
                          handleChange(e);
                          setInitialValues({
                            ...initialValues,
                            absoluteStartDate: e.target.value,
                          });
                        }}
                      />
                      <div className=" me-2 align-self-center">
                        {translate(LanguageKey.to_key)}
                      </div>
                      <Form.Control
                        name="absoluteEndDate"
                        value={values.absoluteEndDate}
                        type="date"
                        className={`${
                          (errors.absoluteStartDate ||
                            errors.absoluteEndDate) &&
                          selectedValue === "BETWEEN_ABSOLUTE"
                        }`}
                        style={{ position: "relative" }}
                        disabled={selectedValue !== "BETWEEN_ABSOLUTE"}
                        onChange={(e) => {
                          handleChange(e);
                          setInitialValues({
                            ...initialValues,
                            absoluteEndDate: e.target.value,
                          });
                        }}
                      />
                    </Col>
                    <Form.Control.Feedback
                      type="invalid"
                      style={{
                        display:
                          errors.absoluteStartDate || errors.absoluteEndDate
                            ? "block"
                            : "none",
                      }}
                    >
                      {(errors.absoluteStartDate || errors.absoluteEndDate) as string}
                    </Form.Control.Feedback>
                  </Row>

                  <Row className="mb-3">
                    <Col>
                      <SntRadio
                        value="BETWEEN_RELATIVE"
                        selectedValue={selectedValue}
                        onChange={(value) => value !== undefined && setSelectedValue(value)}
                        label={translate(LanguageKey.in_timerange_key)}
                      />
                      <IcomoonIcon
                        className="float-end"
                        icon="question"
                        title={translate(LanguageKey.date_filter_title_key)}
                        size={16}
                      />
                    </Col>
                  </Row>
                  <Row className="mb-3">
                    <Col className="d-flex justify-content-between">
                      <div className=" me-2 align-self-center">
                        {" "}
                        {translate(LanguageKey.from_key)}{" "}
                      </div>
                      <Form.Control
                        name="relativeStartDate"
                        value={values.relativeStartDate}
                        placeholder="-1M1w"
                        type="text"
                        className={` me-2 ${
                          (errors.relativeStartDate ||
                            errors.relativeEndDate) &&
                          selectedValue === "BETWEEN_RELATIVE"
                        }`}
                        style={{ position: "relative" }}
                        disabled={selectedValue !== "BETWEEN_RELATIVE"}
                        onChange={(e) => {
                          handleChange(e);
                          setInitialValues({
                            ...initialValues,
                            relativeStartDate: e.target.value,
                          });
                        }}
                      />
                      <div className=" me-2 align-self-center">
                        {translate(LanguageKey.till_key)}
                      </div>
                      <Form.Control
                        name="relativeEndDate"
                        placeholder="+1h2m"
                        value={values.relativeEndDate}
                        type="text"
                        className={`${
                          (errors.relativeStartDate ||
                            errors.relativeEndDate) &&
                          selectedValue === "BETWEEN_RELATIVE"
                        }`}
                        style={{ position: "relative" }}
                        disabled={selectedValue !== "BETWEEN_RELATIVE"}
                        onChange={(e) => {
                          handleChange(e);
                          setInitialValues({
                            ...initialValues,
                            relativeEndDate: e.target.value,
                          });
                        }}
                      />
                    </Col>
                    <Form.Control.Feedback
                      type="invalid"
                      style={{
                        display:
                          errors.relativeStartDate || errors.relativeEndDate
                            ? "block"
                            : "none",
                      }}
                    >
                      {(errors.relativeStartDate || errors.relativeEndDate) as string}
                    </Form.Control.Feedback>
                  </Row>

                  <Row className="mb-3">
                    <Col>
                      <SntRadio
                        value="MONTH"
                        selectedValue={selectedValue}
                        onChange={(value) => value !== undefined && setSelectedValue(value)}
                        label={translate(LanguageKey.v41_month_key)}
                      />
                      <IcomoonIcon
                        className="float-end"
                        icon="question"
                        size={16}
                        title={translate(LanguageKey.date_filter_title_key)}
                      />
                    </Col>
                  </Row>
                  <Row className="mb-3">
                    <Col>
                      <Form.Select
                        name="month"
                        value={values.month}
                        className="float-start"
                        disabled={selectedValue !== "MONTH"}
                        onChange={(e) => {
                          handleChange(e);
                          setInitialValues({
                            ...initialValues,
                            month: e.target.value,
                          });
                        }}
                        style={{ width: "9rem" }}
                      >
                        {optionsMonths.map((month) => (
                          <option key={month.value} value={month.value}>
                            {month.label}
                          </option>
                        ))}
                      </Form.Select>
                    </Col>
                  </Row>
                  <Row className="mb-3">
                    <Col>
                      <SntRadio
                        value="NULL"
                        selectedValue={selectedValue}
                        onChange={(value) => value !== undefined && setSelectedValue(value)}
                        label={translate(LanguageKey.has_no_value_key)}
                      />
                    </Col>
                  </Row>
                </Container>

                <GeneralFooterFilter
                  onClearFilter={() => onClearFilter()}
                  onAppliedFilter={() => {
                    if (errors && Object.keys(errors).length === 0) {
                      handleSubmit();
                    }
                  }}
                  isNotFilter={notFilter}
                  onCheckNotFilter={(e) => {
                    setNotFilter(e);
                  }}
                  disableCheckBox={!selectedValue}
                />
              </form>
            );
          }}
        </Formik>
      </FilterBody>
    </AdvanceFilter>
  );
};

export default DateFilter;
